import { useState, useEffect, useMemo } from "react";
import CrossIcon from "../../../assets/icons/maid-module-icons/CrossIcon.svg";
import BlueArrow from "../../../assets/icons/maid-module-icons/BlueArrow.svg";
import EditIcon from "../../../assets/icons/maid-module-icons/EditIcon.svg";
import BuildingIcon from "../../../assets/icons/maid-module-icons/buildingsIcon.svg";
import { commonAPI } from "../../../services/CommonAPIService";
import UpdateDetailsModal from "../modals/components/UpdateDetails";
import UpdateDocument from "../modals/components/UpdateDocument";
import UpdateFlats from "../modals/components/UpdateFlats";
import UpdateMobileNumber from "./components/UpdateMobile"; 
import UpdateAddStaffModal from "./components/UpdateAddStaffModal";
import ImagePreviewModal from "../modals/modals/ImagePreviewModal";
import { toast } from "react-toastify";

// ── Helper: derive doc type from doc object ──────────────────
const getDocType = (doc) => {
  const id    = (doc?.docCode || doc?.id    || "").toLowerCase();
  const label = (doc?.docName || doc?.label || "").toLowerCase();
  if (id === "aadhaar" || label.includes("aadhaar") || label.includes("aadhar")) return "aadhaar";
  if (id === "pan"     || label.includes("pan"))                                  return "pan";
  if (label.includes("voter") || label.includes("epic"))                          return "voter";
  return "other";
};

// ── Helper: masked prefix per doc type ──────────────────────
const MASKED_PREFIX = {
  aadhaar : "**** **** ",
  pan     : "****** ",
  voter   : "****** ",
  other   : "****** ",
};

// ── Helper: format document number ──────────────────────────
//   masked=true  → "**** **** 1234"  (last 4 only)
//   masked=false → full number as-is
const formatDocNumber = (docNumber, docType, masked) => {
  if (!docNumber) return "—";
  const str = String(docNumber);
  if (!masked) return str;
  const last4  = str.slice(-4);
  const prefix = MASKED_PREFIX[docType] ?? "**** ";
  return prefix + last4;
};

// ── parseMaskedSetting ───────────────────────────────────────
const parseMaskedSetting = (value) =>
  value === true || value === 1 || value === "1" || value === "true" ||
  String(value).toLowerCase() === "true";


const EditModal = ({ show, onHide, staffId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [staffDetails, setStaffDetails] = useState([]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEditId, setShowEditId] = useState(false);
  const [showEditFlats, setShowEditFlats] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showEditMobile, setShowEditMobile] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // Mobile masking (SERVICE_STAFF_MOBILE_NBR_DIALER_VISIBILITY)
  const [showMasked, setShowMasked] = useState(false);

  // Document number masking (SHOW_IDPROOF_MASKED)  ← NEW
  const [showDocMasked, setShowDocMasked] = useState(false);

  const [settingsLoading, setSettingsLoading] = useState(true);


  const myUserData = useMemo(() => {
    const auth = localStorage.getItem("userData");
    if (!auth) return null;
    return JSON.parse(auth);
  }, []);

  const GetServiceStaffDetails = async () => {
    if (!myUserData) return;

    let requestPayload = { staffId: staffId };

    try {
      setLoading(true);

      const response = await commonAPI(
        "GetServiceStaffDetail",
        requestPayload,
        { buildingId: myUserData?.buildingId }
      );

      if (response?.success === true) {
        setStaffDetails({ ...response?.data, staffId } || []);
        onSuccess?.();
      } else {
        throw new Error(response?.message || "Failed to load staff details");
      }

    } catch (error) {
      console.error("GetServiceStaffDetail Error:", error);

      const bodyError = {
        userId: myUserData?.userId || null,
        mobileNumber: myUserData?.mobileNumber || null,
        requestJSON: JSON.stringify(requestPayload),
        uri: "/api/Master/GetServiceStaffDetail",
        errorSource: "CuroNextPortal",
        errorNumber: "EDITMDL02",
        errorSummary: error?.message,
        errorDescription: JSON.stringify(error),
        fileClassName: "EditModal.js",
        functionName: "GetServiceStaffDetails",
      };

      try {
        await commonAPI("ErrorLogMaster", bodyError);
      } catch (logErr) {
        console.error("Error while logging error:", logErr);
      }

      toast.error(error?.message || "Error fetching staff details");
    } finally {
      setLoading(false);
    }
  };

  // ── Load both app settings in one call ──────────────────────
  useEffect(() => {
    const loadSetting = async () => {
      const auth = localStorage.getItem("userData");

      if (!auth) {
        setSettingsLoading(false);
        return;
      }

      const userData = JSON.parse(auth);

      if (!userData?.buildingId) {
        setSettingsLoading(false);
        return;
      }

      try {
        const response = await commonAPI("AppSettings", undefined, {
          buildingId: userData.buildingId,
        });

        if (response?.success === true && Array.isArray(response.data)) {
          // ── Mobile masking ──
          const mobileSetting = response.data.find(
            item => item.key === "SERVICE_STAFF_MOBILE_NBR_DIALER_VISIBILITY"
          );
          const roles = mobileSetting?.value?.toLowerCase()?.split("|") || [];
          setShowMasked(!roles.includes("admin"));

          // ── Document number masking ──  ← NEW
          const docSetting = response.data.find(
            item => item.key === "SHOW_IDPROOF_MASKED"
          );
          setShowDocMasked(parseMaskedSetting(docSetting?.value));

        } else {
          throw new Error(response?.message || "Failed to load AppSettings");
        }

      } catch (error) {
        console.error("AppSettings Error:", error);

        const bodyError = {
          userId: userData?.userId || null,
          mobileNumber: userData?.mobileNumber || null,
          requestJSON: null,
          uri: "/api/Master/AppSettings",
          errorSource: "CuroNextPortal",
          errorNumber: "EDITMDL01",
          errorSummary: error?.message,
          errorDescription: JSON.stringify(error),
          fileClassName: "EditModal.js",
          functionName: "loadSetting",
        };

        try {
          await commonAPI("ErrorLogMaster", bodyError);
        } catch (logErr) {
          console.error("Error while logging error:", logErr);
        }

        setShowMasked(false);
        setShowDocMasked(false);   // ← safe fallback
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSetting();
  }, []);


  const maskMobileNumber = (number) => {
    if (!number) return "—";
    const str = String(number);
    if (str.length < 4) return str;
    return str.slice(0, 2) + "*".repeat(str.length - 4) + str.slice(-2);
  };


  useEffect(() => {
    if (show && staffId && myUserData) {
      GetServiceStaffDetails();
    }
  }, [show, staffId, myUserData]);

  if (!show) return null;

  const handleIdEdit = (document) => {
    setSelectedDocument(document);
    setShowEditId(true);
  };

  const openPreview = (url, title) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
    setShowModal(true);
  };

  const handleClosePreview = () => {
    setShowModal(false);
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
    setPreviewTitle("");
  };



  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 1060,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1070,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 16px",
        }}
      >
        <div
          className="bg-white d-flex flex-column"
          style={{
            width: "100%",
            maxWidth: 600,
            height: "96vh",
            maxHeight: "calc(100vh - 32px)",
            borderRadius: 18,
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {/* Header */}
          <div
            className="d-flex justify-content-between align-items-center text-white"
            style={{
              backgroundColor: "#1C80D2",
              padding: "12px 16px",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <h5
                className="mb-0"
                style={{
                  fontSize: "clamp(14px, 4vw, 18px)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Service Staff Details
              </h5>
            </div>
            <button
              className="btn p-0 d-flex align-items-center justify-content-center bg-white"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                flexShrink: 0,
              }}
              onClick={onHide}
            >
              <img src={CrossIcon} alt="cross" />
            </button>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="loaderDiv">
              <div className="loader"></div>
            </div>
          ) : (
            <>
              {/* Body */}
              <div
                className="flex-grow-1"
                style={{
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  padding: "16px",
                }}
              >
                {/* Profile Section */}
                <div
                  style={{
                    position: "relative",
                    background: "#fff",
                    border: "1px solid #C4C4C4",
                    borderRadius: 12,
                    padding: "16px",
                    marginBottom: 24,
                  }}
                >
                  <img
                    onClick={() => setShowEditProfile(true)}
                    src={EditIcon}
                    alt="Edit"
                    style={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      width: 22,
                      height: 22,
                      cursor: "pointer",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "clamp(12px, 3vw, 16px)",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={staffDetails.userImageURL}
                        alt="Profile"
                        onClick={() => openPreview(staffDetails.userImageURL, "Profile Photo")}
                        style={{
                          width: "clamp(70px, 15vw, 85px)",
                          height: "clamp(70px, 15vw, 85px)",
                          borderRadius: "50%",
                          border: "2px solid #e5e7eb",
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: "150px" }}>
                      <div className="mb-2">
                        <label className="form-label fw-bold mb-2">{staffDetails.userName}</label>
                      </div>
                      <div
                        style={{
                          fontSize: "clamp(12px, 2.5vw, 13px)",
                          color: "#6b7280",
                          fontWeight: 500,
                          marginTop: 2,
                        }}
                      >
                        {staffDetails.gender}
                      </div>
                    </div>
                  </div>
                </div>

                {/* CuroNext ID */}
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      position: "relative",
                      border: "2px solid #EDEDED",
                      borderRadius: 12,
                      padding: "40px 16px 24px",
                      background: "#f0f9ff",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: 16,
                        background: "#f0f9ff",
                        padding: "0 6px",
                        fontSize: "clamp(11px, 2.5vw, 12px)",
                        fontWeight: 500,
                        color: "#454545",
                      }}
                    >
                      CuroNext ID (Pass Code)
                    </div>
                    <div
                      style={{
                        border: "3px dashed #2295D2",
                        borderRadius: 12,
                        padding: "clamp(16px, 4vw, 24px)",
                        textAlign: "center",
                        background: "#f0f9ff",
                        width: "100%",
                        maxWidth: "300px",
                        margin: "0 auto",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "10px",
                          fontSize: "clamp(20px, 6vw, 22px)",
                          fontWeight: "bold",
                          color: "#2295D2",
                        }}
                      >
                        {(() => {
                          const code = String(staffDetails.passCode || "-");
                          if (code === "-") return <span>{code}</span>;
                          const chunks = code.match(/.{1,3}/g) || [];
                          return chunks.map((chunk, index) => (
                            <span key={index} style={{ letterSpacing: 5 }}>
                              {chunk}
                            </span>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service Type */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "clamp(12px, 2.5vw, 13px)",
                      fontWeight: 500,
                      color: "#595757",
                      marginBottom: 12,
                    }}
                  >
                    Service Type
                  </label>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "12px 50px 12px 14px",
                      border: "1px solid #C4C4C4",
                      borderRadius: 8,
                      fontSize: "clamp(13px, 3vw, 14px)",
                      color: "#111827",
                      background: "#fff",
                    }}
                  >
                    <img
                      onClick={() => setShowEditRole(true)}
                      src={EditIcon}
                      alt="Edit"
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 14,
                        width: 22,
                        height: 22,
                        cursor: "pointer",
                      }}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {staffDetails?.roleName}
                    </span>
                  </div>
                </div>

                {/* Mobile Number */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "clamp(12px, 2.5vw, 13px)",
                      fontWeight: 500,
                      color: "#595757",
                      marginBottom: 12,
                    }}
                  >
                    Mobile Number
                  </label>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "12px 50px 12px 14px",
                      border: "1px solid #C4C4C4",
                      borderRadius: 8,
                      fontSize: "clamp(13px, 3vw, 14px)",
                      color: "#111827",
                      background: "#fff",
                    }}
                  >
                    <img
                      onClick={() => setShowEditMobile(true)}
                      src={EditIcon}
                      alt="Edit"
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 14,
                        width: 22,
                        height: 22,
                        cursor: "pointer",
                      }}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {settingsLoading
                        ? "..."
                        : showMasked
                        ? maskMobileNumber(staffDetails?.mobileNumber)
                        : staffDetails?.mobileNumber || "No mobile number"}
                    </span>
                  </div>
                </div>

                {/* Document Details */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "clamp(12px, 2.5vw, 13px)",
                      fontWeight: 500,
                      color: "#595757",
                      marginBottom: 12,
                    }}
                  >
                    Document Details
                  </label>

                  {staffDetails?.lstDocuments && staffDetails.lstDocuments.length > 0 ? (
                    staffDetails.lstDocuments.map((document, index) => (
                      <div
                        key={document.staffXRefDocId || index}
                        style={{
                          position: "relative",
                          background: "#fff",
                          border: "1px solid #C4C4C4",
                          borderRadius: 12,
                          padding: 16,
                          marginBottom: index < staffDetails.lstDocuments.length - 1 ? 16 : 0,
                        }}
                      >
                        <img
                          onClick={() => handleIdEdit(document)}
                          src={EditIcon}
                          alt="Edit"
                          style={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            width: 22,
                            height: 22,
                            cursor: "pointer",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: "clamp(12px, 2.5vw, 13px)",
                            color: "#111827",
                            marginBottom: 12,
                            fontWeight: 500,
                          }}
                        >
                          <img
                            src={document.thumbnailImageURL}
                            alt={document.docCode}
                            style={{
                              width: "clamp(30px, 6vw, 35px)",
                              height: "auto",
                              borderRadius: 6,
                              objectFit: "cover",
                            }}
                          />
                          <span>{document.docName}</span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "clamp(12px, 3vw, 16px)",
                            flexWrap: "wrap",
                          }}
                        >
                          <img
                            src={document.docURL}
                            alt={document.docName}
                            onClick={() => openPreview(document.docURL, `${document.docName} Preview`)}
                            style={{
                              width: "clamp(120px, 25vw, 150px)",
                              height: "auto",
                              borderRadius: 6,
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                          />

                          {/* ── Document number: masked or full ── */}
                          <div
                            style={{
                              fontSize: "clamp(15px, 3.5vw, 17px)",
                              fontWeight: 700,
                              color: "#111827",
                              letterSpacing: 1,
                              whiteSpace: "nowrap",
                              fontFamily: "monospace",
                            }}
                          >
                            {settingsLoading
                              ? "..."
                              : formatDocNumber(
                                  document.docNumber,
                                  getDocType(document),
                                  showDocMasked
                                )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #C4C4C4",
                        borderRadius: 12,
                        padding: 16,
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      No documents available
                    </div>
                  )}
                </div>

                {/* Flats */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "clamp(12px, 2.5vw, 13px)",
                      fontWeight: 500,
                      color: "#595757",
                      marginBottom: 12,
                    }}
                  >
                    Flats
                  </label>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "12px 50px 12px 14px",
                      border: "1px solid #C4C4C4",
                      borderRadius: 8,
                      fontSize: "clamp(13px, 3vw, 14px)",
                      color: "#111827",
                      background: "#fff",
                    }}
                  >
                    <img
                      onClick={() => setShowEditFlats(true)}
                      src={EditIcon}
                      alt="Edit"
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 14,
                        width: 22,
                        height: 22,
                        cursor: "pointer",
                      }}
                    />
                    <img
                      src={BuildingIcon}
                      alt="Building"
                      style={{
                        width: "clamp(16px, 4vw, 18px)",
                        height: "auto",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {staffDetails?.lstFlats && staffDetails.lstFlats.length > 0
                        ? staffDetails.lstFlats
                            .map((flat) => `${flat.towerName}-${flat.flatName}`)
                            .join(", ")
                        : "No flats assigned"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cancel Button */}
              <div style={{ padding: "16px" }}>
                <button
                  onClick={onHide}
                  className="btn w-100"
                  style={{
                    background: "#FFFFFF",
                    color: "#1C80D2",
                    borderRadius: 30,
                    padding: "clamp(12px, 3vw, 14px)",
                    fontWeight: 600,
                    fontSize: "clamp(14px, 3vw, 15px)",
                    border: "1px solid #1C80D2",
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Update Profile Modal */}
      {showEditProfile && (
        <UpdateDetailsModal
          show={showEditProfile}
          onHide={() => setShowEditProfile(false)}
          staffDetails={staffDetails}
          onSuccess={() => {
            GetServiceStaffDetails();
            onSuccess?.();
          }}
        />
      )}

      {/* Update Document Modal */}
      {showEditId && (
        <UpdateDocument
          show={showEditId}
          onHide={() => {
            setShowEditId(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
          staffId={staffId}
          onSuccess={() => {
            GetServiceStaffDetails();
            onSuccess?.();
          }}
        />
      )}

      {/* Update Flats Modal */}
      {showEditFlats && (
        <UpdateFlats
          show={showEditFlats}
          onHide={() => setShowEditFlats(false)}
          staffDetails={staffDetails}
          staffId={staffId}
          onSuccess={() => {
            GetServiceStaffDetails();
            onSuccess?.();
          }}
        />
      )}

      {/* Update Mobile Modal */}
      {showEditMobile && (
        <UpdateMobileNumber
          show={showEditMobile}
          onHide={() => setShowEditMobile(false)}
          currentMobile={staffDetails.mobileNumber}
          staffId={staffId}
          showAccessWarning={Boolean(
            staffDetails?.userId && staffDetails?.userMappingId
          )}
          onSuccess={() => {
            GetServiceStaffDetails();
            onSuccess?.();
          }}
        />
      )}

      {/* Update Role Modal */}
      {showEditRole && (
        <UpdateAddStaffModal
          show={showEditRole}
          onClose={() => setShowEditRole(false)}
          staffId={staffId}
          currentRoleId={staffDetails.roleId}
          onSuccess={() => {
            GetServiceStaffDetails();
            onSuccess?.();
          }}
        />
      )}

      <ImagePreviewModal
        show={showModal}
        imageUrl={previewUrl}
        onClose={handleClosePreview}
        title={previewTitle}
      />
    </>
  );
};

export default EditModal;